// Pure data — no icon component values, so this stays server-safe.

export const QUICK_ANSWER =
  "Choose Proxidize for predictable flat pricing with no monthly minimum — $2/GB mobile, $1/GB residential — which beats SOAX below roughly 750 GB/month of mobile or 3,000 GB/month of residential in Tier 1 countries. Choose SOAX for coverage across 195+ geolocations, broader protocol support (UDP, QUIC), or committed enterprise volume above those thresholds, where its tiered pricing drops below Proxidize's flat rate.";

export const KEY_TAKEAWAYS = [
  "Proxidize is cheaper than SOAX at typical volumes. $200 vs $300 for 100 GB of mobile on SOAX's Builder plan (Tier 1 countries).",
  "SOAX gets cheaper at committed enterprise volume. Its Scale ($1.50/GB) and Enterprise ($0.85/GB) tiers require $1,500–3,000/month minimums that can undercut Proxidize above roughly 750 GB/month mobile or 3,000 GB/month residential.",
  "SOAX prices mobile and residential on the same ladder. One rate covers both products; Proxidize prices them separately at flat rates.",
  "SOAX covers far more countries under one network. 195+ geolocations versus Proxidize's five mobile countries (US, Canada, UK, Germany, India).",
  "SOAX supports more protocols. HTTP, HTTPS, SOCKS5, UDP, and QUIC, versus Proxidize's HTTP(S), SOCKS5, and HTTP/3 + QUIC.",
  "Neither provider requires a monthly minimum to start. SOAX's Sandbox plan and Proxidize's per-GB plans both let you begin without a committed spend.",
];

export interface VerdictCard {
  provider: "proxidize" | "soax";
  title: string;
  points: string[];
}

export const VERDICT_CARDS: VerdictCard[] = [
  {
    provider: "proxidize",
    title: "Choose Proxidize if you need",
    points: [
      "Premium pooled mobile IPs with city-level targeting in Proxidize's covered countries (US, Canada, UK, Germany, India)",
      "A flat $2/GB mobile rate with no monthly minimum — cheaper than SOAX at typical volumes (100 GB: $200 vs $300 on SOAX's Builder plan)",
      "A per-proxy mobile plan from $59/proxy/month with unlimited bandwidth — SOAX has no per-proxy mobile option",
      "Residential proxies in 195+ countries at a flat $1/GB with unused bandwidth rollover",
      "Manual/bulk rotation, an API rotation URL, and custom interval control",
      "Self-serve setup with free sign-up and no minimum monthly spend",
    ],
  },
  {
    provider: "soax",
    title: "Choose SOAX if you need",
    points: [
      "Global mobile and residential coverage across 195+ geolocations with city and ASN targeting under one network",
      "Broader protocol support: HTTP, HTTPS, SOCKS5, UDP, and QUIC",
      "Published 99.95% success rate and 0.55s response time SLAs",
      "Committed-spend enterprise pricing that drops to $0.85/GB at $3,000/month, which can beat Proxidize's flat rate at very high volume",
      "One account and one rate for both residential and mobile traffic",
    ],
  },
];

export interface ComparisonRow {
  feature: string;
  proxidize: string | boolean;
  soax: string | boolean;
}

export const COMPARISON_ROWS: ComparisonRow[] = [
  {
    feature: "Best for",
    proxidize: "Mobile IPs and residential proxies, self-serve simplicity, no minimum spend",
    soax: "Global mobile + residential network with advanced protocol and session control",
  },
  {
    feature: "Product breadth",
    proxidize: "Mobile and residential live; ISP and datacenter coming soon",
    soax: "Residential and mobile under one unified network and one price ladder",
  },
  {
    feature: "Mobile network",
    proxidize: "5G/4G/3G/LTE premium pooled mobile IPs, 50+ locations across multiple countries",
    soax: "5G/4G/3G/LTE mobile IPs across 195+ geolocations",
  },
  {
    feature: "Country coverage",
    proxidize: "Mobile: US, Canada, UK, Germany, India. Residential: 195+ countries",
    soax: "195+ countries for both mobile and residential, across 3 pricing tiers by country",
  },
  {
    feature: "Targeting",
    proxidize: "City + ASN",
    soax: "Country, region, city, or ASN",
  },
  {
    feature: "Session control",
    proxidize: "Sticky or random per access point",
    soax: "Sticky sessions up to 60 minutes, or rotate on every request",
  },
  {
    feature: "Protocols",
    proxidize: "HTTP(S), SOCKS5, HTTP/3 + QUIC",
    soax: "HTTP, HTTPS, SOCKS5, UDP, and QUIC",
  },
  {
    feature: "Pricing model",
    proxidize: "Flat per-GB rate, no monthly minimum",
    soax: "Tiered plans, each with a monthly minimum commitment from $0 to $3,000",
  },
  {
    feature: "Mobile pricing (Tier 1: US, UK, Canada, Germany)",
    proxidize: "Flat $2/GB (plans from 25 GB to 10,000 GB)",
    soax: "$5.00/GB (Sandbox, no minimum) down to $0.85/GB (Enterprise, $3,000/mo minimum) — same rate as residential",
  },
  {
    feature: "Mobile pricing (Tier 2: India)",
    proxidize: "Flat $2/GB (plans from 25 GB to 10,000 GB)",
    soax: "$3.75/GB (Sandbox, no minimum) down to $0.55/GB (Enterprise, $3,000/mo minimum) — cheaper than SOAX's Tier 1 rate",
  },
  {
    feature: "Per-proxy mobile",
    proxidize: "From $59/proxy/month, unlimited bandwidth",
    soax: "Not offered — bandwidth-only shared pool",
  },
  {
    feature: "100 GB monthly cost (Tier 1)",
    proxidize: "$200/mo ($2.00/GB)",
    soax: "$300/mo on the Builder plan ($3.00/GB effective)",
  },
  {
    feature: "1,000 GB monthly cost (Tier 1)",
    proxidize: "$2,000/mo ($2.00/GB)",
    soax: "$1,500/mo on the Scale plan ($1.50/GB effective) — SOAX is cheaper at this volume",
  },
  {
    feature: "Free / low-cost testing",
    proxidize: "Free sign-up, no credit card",
    soax: "$1.99 trial (400 MB over 3 days), or pay-as-you-go on the no-minimum Sandbox plan",
  },
  { feature: "Self-serve signup", proxidize: true, soax: true },
  {
    feature: "Residential network",
    proxidize: "Millions of IPs, 195+ countries, ethically sourced",
    soax: "195+ geolocations with city-level targeting, same network as mobile",
  },
  {
    feature: "Residential pricing",
    proxidize: "Flat $1/GB (plans from 25 GB to 10,000 GB)",
    soax: "Same tiered ladder as mobile: $5.00 down to $0.85/GB",
  },
  {
    feature: "50 GB residential plan",
    proxidize: "$50/mo ($1.00/GB)",
    soax: "$200/mo minimum on the Builder plan ($4.00/GB effective at this volume)",
  },
  {
    feature: "Unused bandwidth",
    proxidize: "Rolls over, doesn't expire",
    soax: "Credits roll over 60 days on monthly billing, 365 days on annual billing",
  },
  { feature: "ISP (static residential) proxies", proxidize: "Not yet offered", soax: false },
  { feature: "Datacenter proxies", proxidize: "Not yet offered", soax: false },
];

export interface UseCaseFit {
  useCase: string;
  fit: "Proxidize" | "SOAX" | "Both";
  reason: string;
}

export const USE_CASE_FITS: UseCaseFit[] = [
  {
    useCase: "Mobile IP workflows in Proxidize's covered countries",
    fit: "Proxidize",
    reason:
      "Premium pooled mobile IPs with city-level targeting, a flat $2/GB rate, and no monthly minimum commitment.",
  },
  {
    useCase: "Global mobile or residential coverage across many countries",
    fit: "SOAX",
    reason:
      "195+ geolocations with city and ASN targeting under one network for both mobile and residential traffic.",
  },
  {
    useCase: "Stable mobile exits for account-based workflows",
    fit: "Proxidize",
    reason:
      "Per-proxy plans from $59/proxy/month include unlimited bandwidth and manual/bulk rotation. SOAX doesn't sell per-proxy mobile IPs at all.",
  },
  {
    useCase: "Raw UDP/QUIC transport requirements",
    fit: "SOAX",
    reason:
      "SOAX runs a dedicated UDP proxy over SOCKS5 plus a QUIC listener — protocol options Proxidize's mobile stack doesn't expose the same way.",
  },
  {
    useCase: "Typical US/UK/Canada/Germany usage under ~750 GB",
    fit: "Proxidize",
    reason:
      "At SOAX's Tier 1 rate (which covers these 4 countries), Proxidize's flat per-GB rate beats every SOAX plan tier below this volume, with no commitment required.",
  },
  {
    useCase: "Committed enterprise volume above ~750 GB/month",
    fit: "SOAX",
    reason:
      "SOAX's Scale and Enterprise tiers drop to $1.50 and $0.85/GB at $1,500–$3,000/month minimums, undercutting Proxidize's flat $2/GB at that scale (Tier 1 countries).",
  },
  {
    useCase: "Mobile traffic mostly from India",
    fit: "SOAX",
    reason:
      "India falls in SOAX's cheaper Tier 2 rate ($3.75 down to $0.55/GB), which undercuts Proxidize's flat $2/GB starting at roughly 250 GB/month, well below the Tier 1 crossover.",
  },
  {
    useCase: "Residential bandwidth at typical volumes",
    fit: "Proxidize",
    reason:
      "A flat $1/GB with no monthly minimum beats every SOAX plan tier below roughly 3,000 GB/month, regardless of country tier.",
  },
  {
    useCase: "Very large committed residential volume",
    fit: "SOAX",
    reason:
      "Above roughly 3,000 GB/month, SOAX's Enterprise tier ($0.85/GB Tier 1, $0.55/GB Tier 2 for India) at a $3,000/month minimum can match or undercut Proxidize's flat $1/GB.",
  },
  {
    useCase: "Residential bandwidth that doesn't go to waste",
    fit: "Both",
    reason:
      "Proxidize rolls over unused bandwidth with no expiry; SOAX's credits roll over for 60 days on monthly billing or 365 days on annual billing.",
  },
];

export interface MigrationStep {
  title: string;
  description: string;
}

export const MIGRATION_STEPS: MigrationStep[] = [
  {
    title: "Create a free account",
    description: "Sign up for free: no credit card, no sales call, no monthly minimum.",
  },
  {
    title: "Pick per-GB or per-proxy",
    description:
      "Per-GB gives flexible access to the full pool billed by data. Per-proxy gives you real mobile IPs of your own with unlimited bandwidth.",
  },
  {
    title: "Create an access point",
    description:
      "Choose a city or random rotation and set sticky or random IP behavior — the same knobs you tuned in SOAX's dashboard.",
  },
  {
    title: "Copy your credentials",
    description:
      "Credentials are generated live in HTTP or SOCKS5 format, with auto-generated cURL examples and formatted proxy list exports.",
  },
  {
    title: "Test before switching traffic",
    description:
      "Run your existing scraper or browser profile against the new endpoint with a small job and compare success rates side by side.",
  },
  {
    title: "Monitor usage in real time",
    description:
      "The built-in analytics dashboard shows bandwidth trends, top domains, and a live connection log per access point.",
  },
];

export const MIGRATION_CURL = `curl -x "http://USERNAME:PASSWORD@HOST:PORT" https://ipinfo.io`;
